import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, FileText, History, XCircle } from 'lucide-react';
import { TriageItem, formatStudyTime } from '../review';

interface TriageQueueProps {
  items: TriageItem[];
  selectedId: string | null;
  editable: boolean;
  /** The class currently being stepped through one event at a time (owned by ReviewWorkspace),
   * so its group can be highlighted here rather than that state living in two places. */
  activeClass: string | null;
  onSelect: (id: string) => void;
  /** Click a class label (not the chevron) to start stepping through just that class, in
   * chronological order, via Prev/Next next to the strip or the j/k keys. */
  onActivateClass: (rhythmClass: string) => void;
  /** Bulk-confirm every unreviewed, non-critical item of one class (must be shown-count confirmed). */
  onBulkConfirm: (rhythmClass: string, count: number) => void;
  /** Bulk-*acknowledge* (not confirm) every open critical of one class — seeing the alarm fired is
   * not the same as accepting it as a true finding, so this is allowed even for must-review classes. */
  onBulkAcknowledge: (rhythmClass: string, count: number) => void;
}

/** A row per item is fine at this scale; well past it, even a collapsed-by-default group that gets
 * manually opened would otherwise dump the whole thing into the DOM at once. */
const ROWS_PER_PAGE = 100;

const STATUS_STYLE: Record<string, string> = {
  UNREVIEWED: 'border-slate-700 text-slate-400',
  CONFIRMED: 'border-emerald-800 bg-emerald-950 text-emerald-300',
  REJECTED: 'border-rose-800 bg-rose-950 text-rose-300',
  EDITED: 'border-amber-800 bg-amber-950 text-amber-300',
};

interface Group {
  rhythmClass: string;
  items: TriageItem[];
  unreviewed: number;
  openCritical: number;
  mustReview: boolean;
}

/**
 * Grouped by rhythm class, not a flat scroll (2026-09-14, from real use: a real recording produced
 * 1,951 episodes and 200 morphology clusters, and a flat list of either is not something a reviewer
 * can finish — the product goal is a full study reviewed in well under an hour, §4.4). A class this
 * noisy is usually the known VF-screen precision problem (~31 false alarms/hour, §11.7), not 1,951
 * separate clinical decisions — grouping is what makes "confirm the rest of this class" one action
 * instead of hundreds.
 */
export const TriageQueue: React.FC<TriageQueueProps> = ({
  items, selectedId, editable, activeClass, onSelect, onActivateClass, onBulkConfirm, onBulkAcknowledge,
}) => {
  const [visibleCount, setVisibleCount] = useState<Record<string, number>>({});
  const groups = useMemo<Group[]>(() => {
    const byClass = new Map<string, TriageItem[]>();
    for (const item of items) {
      const list = byClass.get(item.rhythm_class) ?? [];
      list.push(item);
      byClass.set(item.rhythm_class, list);
    }
    const built = Array.from(byClass.entries()).map(([rhythmClass, groupItems]) => ({
      rhythmClass,
      items: groupItems,
      unreviewed: groupItems.filter((i) => i.status === 'UNREVIEWED').length,
      openCritical: groupItems.filter((i) => i.is_critical && !i.critical_acknowledged_at && i.status !== 'REJECTED').length,
      mustReview: groupItems[0]?.must_review ?? false,
    }));
    // Open criticals first, then must-review classes, then by how much work is left.
    built.sort((a, b) =>
      b.openCritical - a.openCritical || Number(b.mustReview) - Number(a.mustReview) || b.unreviewed - a.unreviewed,
    );
    return built;
  }, [items]);

  // Small groups start open; large ones start collapsed on size alone — *including* ones holding
  // open criticals. The first version exempted those, on the reasoning that criticals must stay
  // visible, but a classifier with a known false-alarm rate (§11.7) makes the noisiest class the
  // most likely to *be* the critical one — so that reasoning forced open exactly the 1,244-item
  // group that made the queue unusable, found 2026-09-14. The group header's warning icon, count and
  // bulk-acknowledge action stay visible either way; the row list is what collapses.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  useEffect(() => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      for (const g of groups) {
        if (!next.has(g.rhythmClass) && g.items.length > 8) next.add(g.rhythmClass);
      }
      return next;
    });
  }, [groups.map((g) => g.rhythmClass).join(',')]);

  const toggle = (cls: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(cls)) next.delete(cls);
      else next.add(cls);
      return next;
    });

  const selectedRef = useRef<HTMLButtonElement | null>(null);
  // `void`, not a bare expression: an effect's return value is its cleanup, and React calls
  // whatever isn't literally `undefined` as a function on unmount. scrollIntoView() is spec'd
  // void, but whatever it actually returned here was not `undefined` — "destroy is not a
  // function," thrown on TriageQueue's very first unmount (2026-09-14). `void` discards the
  // return value unconditionally, the same guard already used for this exact shape in
  // UserManagement.tsx and ReviewWorkspace.tsx — this was the one place that had been missed.
  useEffect(() => void selectedRef.current?.scrollIntoView({ block: 'nearest' }), [selectedId]);
  const totalUnreviewed = items.filter((i) => i.status === 'UNREVIEWED').length;

  return (
    <div className="glass-panel flex min-h-0 flex-1 flex-col rounded-2xl">
      <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3 text-xs">
        <span className="font-bold uppercase tracking-wide text-white">Triage queue</span>
        <span className="mono text-slate-400">{totalUnreviewed} / {items.length} open</span>
      </div>
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-2">
        {items.length === 0 && (
          <p className="p-4 text-xs text-slate-400">
            The engine reported no rhythm episodes. That is an engine result to be confirmed against the
            overview and strips, not a clinical conclusion.
          </p>
        )}
        {groups.map((group) => {
          const isCollapsed = collapsed.has(group.rhythmClass);
          const isActive = group.rhythmClass === activeClass;
          const bulkConfirmable = editable && !group.mustReview && group.unreviewed > 0;
          const bulkAckable = editable && group.openCritical > 0;
          const shown = visibleCount[group.rhythmClass] ?? ROWS_PER_PAGE;
          return (
            <div key={group.rhythmClass} className={`rounded-xl border bg-slate-900/40 ${isActive ? 'border-cyan-600' : 'border-slate-800'}`}>
              {/* Stacked, not a single row (2026-09-21, second attempt).
                  The first attempt added `min-w-0` so the row could shrink instead of clipping —
                  but `flex-1 min-w-0` lets it shrink to *zero*, and the `shrink-0` MUST REVIEW
                  badge then overflowed onto the Acknowledge button while `truncate` collapsed the
                  class name to nothing. The class name — the one thing in the row that matters —
                  disappeared entirely.

                  In a ~250px panel, "possible_asystole · MUST REVIEW · 265/265 open, 265 unack. ·
                  [Acknowledge (265)]" does not fit on one line at any shrink setting, so it no
                  longer tries. Three lines that always fit beat one line that sometimes does. */}
              <div className="flex flex-col gap-1 px-2.5 py-2">
                <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs font-bold uppercase">
                  <button
                    onClick={() => toggle(group.rhythmClass)}
                    title={isCollapsed ? 'Expand' : 'Collapse'}
                    className="shrink-0 text-slate-500 hover:text-slate-300"
                  >
                    {isCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </button>
                  <button
                    onClick={() => {
                      setCollapsed((prev) => {
                        const next = new Set(prev);
                        next.delete(group.rhythmClass);
                        return next;
                      });
                      onActivateClass(group.rhythmClass);
                    }}
                    title={`Step through ${group.rhythmClass} one event at a time, in chronological order`}
                    className={`flex items-center gap-1.5 text-left hover:text-white ${isActive ? 'text-cyan-300' : 'text-white'}`}
                  >
                    {group.openCritical > 0 && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-rose-400" />}
                    {/* `break-all`, not `truncate`: `supraventricular_triplet` is one unbroken
                        token, so wrapping it is the only way to show all of it — and a class name
                        the reviewer cannot read is the one thing this panel must never do. */}
                    <span className="break-all">{group.rhythmClass}</span>
                  </button>
                  {group.mustReview && (
                    <span className="shrink-0 rounded border border-rose-900 px-1 text-[9px] text-rose-300">
                      MUST REVIEW
                    </span>
                  )}
                </div>
                {/* `whitespace-nowrap` so the counts wrap as a unit rather than breaking
                    mid-number — "6032/60" on one line and "32 open" on the next is worse than
                    clipping, because it reads as a different number. */}
                <span className="mono whitespace-nowrap text-[10px] uppercase text-slate-500">
                  {group.unreviewed}/{group.items.length} open
                  {group.openCritical > 0 && `, ${group.openCritical} unack.`}
                </span>
                {(bulkAckable || bulkConfirmable) && (
                <span className="flex flex-wrap gap-1.5">
                  {bulkAckable && (
                    <button
                      onClick={() => {
                        // Same risk as the critical banner's own acknowledge-all (U8, §13.6): one
                        // click here clears every open critical finding in the class at once, and
                        // a small inline button does not look like that. Same confirm pattern used
                        // there and elsewhere for a consequence the button alone doesn't convey.
                        if (group.openCritical > 1) {
                          const ok = window.confirm(
                            `Acknowledge all ${group.openCritical} open critical ${group.rhythmClass} findings?\n\n` +
                              'This clears every one of them from the queue at once. Acknowledging ' +
                              'means a human has seen the alarm fire — it is not the same as confirming ' +
                              'each finding as real, and it cannot be undone in bulk.',
                          );
                          if (!ok) return;
                        }
                        onBulkAcknowledge(group.rhythmClass, group.openCritical);
                      }}
                      className="rounded border border-rose-800 px-2 py-0.5 text-[10px] font-semibold text-rose-300 hover:border-rose-500"
                      title={`Acknowledge — not confirm — the ${group.openCritical} open critical ${group.rhythmClass} finding(s)`}
                    >
                      Acknowledge ({group.openCritical})
                    </button>
                  )}
                  {bulkConfirmable && (
                    <button
                      onClick={() => onBulkConfirm(group.rhythmClass, group.unreviewed)}
                      className="rounded border border-slate-700 px-2 py-0.5 text-[10px] font-semibold text-slate-300 hover:border-emerald-600 hover:text-emerald-300"
                      title={`Confirm the remaining ${group.unreviewed} unreviewed ${group.rhythmClass} finding(s)`}
                    >
                      Confirm rest ({group.unreviewed})
                    </button>
                  )}
                </span>
                )}
              </div>

              {!isCollapsed && (
                <div className="space-y-1 border-t border-slate-800/80 p-1.5">
                  {group.items.slice(0, shown).map((item) => {
                    const selected = item.id === selectedId;
                    const criticalOpen = item.is_critical && !item.critical_acknowledged_at && item.status !== 'REJECTED';
                    return (
                      <button
                        key={item.id}
                        ref={selected ? selectedRef : undefined}
                        onClick={() => onSelect(item.id)}
                        className={`w-full rounded-lg border p-2 text-left text-xs transition-all ${
                          selected ? 'border-cyan-500 bg-cyan-950/40' : 'border-slate-800 bg-slate-900/60 hover:border-slate-600'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="flex items-center gap-1.5 text-slate-300">
                            {criticalOpen && <AlertTriangle className="h-3 w-3 text-rose-400" />}
                            <span className="mono">{formatStudyTime(item.onset_seconds)}</span>
                          </span>
                          <span className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_STYLE[item.status]}`}>
                            {item.status === 'CONFIRMED' && <CheckCircle2 className="mr-1 inline h-3 w-3" />}
                            {item.status === 'REJECTED' && <XCircle className="mr-1 inline h-3 w-3" />}
                            {item.status}
                          </span>
                        </div>
                        <div className="mono mt-1 flex items-center justify-between text-slate-400">
                          <span>{Number(item.duration_seconds).toFixed(1)} s{item.avg_rate_bpm ? ` · ${Math.round(Number(item.avg_rate_bpm))} bpm` : ''}</span>
                          <span className="flex items-center gap-1">
                            {item.selected_for_report && <FileText className="h-3 w-3 text-cyan-400" />}
                            {(Number(item.confidence) * 100).toFixed(0)}%
                          </span>
                        </div>
                        {/* What this reviewer decided about this stretch of signal in an earlier
                            run (DESIGN.md D54). Shown as history, deliberately not as a state:
                            the badge above still reads UNREVIEWED, because it is, and this
                            finding still blocks signature until someone judges *it*. The engine
                            version is here because a better engine may legitimately disagree
                            with a correction made against a worse one — without it, accepting
                            the proposal is a reflex rather than a decision. */}
                        {item.proposal && (
                          <div className="mt-1.5 flex items-start gap-1.5 rounded border border-amber-900/60 bg-amber-950/30 px-1.5 py-1 text-[10px] leading-tight text-amber-200/90">
                            <History className="mt-px h-3 w-3 shrink-0 text-amber-400" />
                            <span>
                              Previously <strong>{item.proposal.verdict}</strong>
                              {item.proposal.human_class ? ` as ${item.proposal.human_class}` : ''}
                              {item.proposal.original_class && item.proposal.original_class !== item.rhythm_class
                                ? ` (then called ${item.proposal.original_class})`
                                : ''}
                              <span className="mono block text-amber-200/50">
                                {item.proposal.engine_version} · not applied
                              </span>
                            </span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                  {group.items.length > shown && (
                    <button
                      onClick={() => setVisibleCount((prev) => ({ ...prev, [group.rhythmClass]: shown + ROWS_PER_PAGE }))}
                      className="w-full rounded-lg border border-dashed border-slate-700 p-2 text-center text-xs text-slate-400 hover:border-slate-500"
                    >
                      Show {Math.min(ROWS_PER_PAGE, group.items.length - shown)} more of {group.items.length - shown} remaining
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
